/** Pure rules of the client order screen: which fields show and count, the progress meter, labels. */
import type { BlockView, ClientField, ClientOrder, ClientSection, FieldSource } from './api';

const isEmpty = (v: unknown) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** Shown for this order: content use only with UGC, exclusivity only when creators post. */
export function fieldShown(f: ClientField, blocks: BlockView[]): boolean {
  if (f.id === 'content_usage' || f.id === 'delivery_formats') return blocks.some((b) => b.kind === 'ugc');
  if (f.id === 'competitor_clause') return blocks.some((b) => b.kind === 'posts');
  return true;
}
/** Counts toward "ready to order" (mirrors the server's clientRequired). */
export function fieldRequired(f: ClientField, blocks: BlockView[]): boolean {
  if (f.required) return true;
  if (f.id === 'content_usage' || f.id === 'delivery_formats') return blocks.some((b) => b.kind === 'ugc');
  if (f.id === 'competitor_clause') return blocks.some((b) => b.kind === 'posts');
  return false;
}

export interface SectionProgress { id: string; title: string; required: number; open: number }
/** Per section: how many answers it needs and how many are still open (the server's gaps decide what is open). */
export function progressOf(sections: ClientSection[], order: Pick<ClientOrder, 'blocks' | 'gaps'>): { sections: SectionProgress[]; required: number; open: number; percent: number } {
  const out = sections.map((s) => {
    const req = s.fields.filter((f) => fieldRequired(f, order.blocks)).length + (s.id === 'offering' ? Math.max(1, order.blocks.length) : 0);
    const open = order.gaps.filter((g) => g.section === s.id).length;
    return { id: s.id, title: s.title, required: req, open: Math.min(open, req) };
  });
  const required = out.reduce((a, s) => a + s.required, 0);
  const open = out.reduce((a, s) => a + s.open, 0);
  return { sections: out, required, open, percent: required ? Math.round(((required - open) / required) * 100) : 0 };
}

export const SOURCE_LABEL: Partial<Record<FieldSource, string>> = {
  brand_os: 'From your Brand OS', history: 'From your past campaigns', account: 'From your account', standard: 'Standard', lykke: 'From your chat with Lykke', file: 'From your file',
};

export const COUNTRY_NAMES: Record<string, string> = {
  SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', GB: 'United Kingdom', DE: 'Germany', NL: 'Netherlands', FR: 'France', ES: 'Spain', IT: 'Italy',
  PL: 'Poland', US: 'United States', IE: 'Ireland', BE: 'Belgium', AT: 'Austria', CH: 'Switzerland', PT: 'Portugal',
};
export const optionLabel = (f: ClientField, o: string) => f.optionLabels?.[o] || (f.kind === 'countries' ? COUNTRY_NAMES[o] || o : o);

/** A gap's anchor in the document: a field, a block, or the offering. */
export const anchorOf = (gapId: string) => (gapId.startsWith('block:') ? `ck-block-${gapId.slice(6)}` : gapId === 'blocks' ? 'ck-section-offering' : `ck-field-${gapId}`);
export { isEmpty };

/** Scroll the order document (only it, never the page around it) to an element; on the phone the page itself scrolls. */
export function scrollToInDoc(elementId: string, block: 'start' | 'center' = 'start'): void {
  const el = document.getElementById(elementId); if (!el) return;
  const doc = document.getElementById('ck-doc');
  if (doc && doc.scrollHeight > doc.clientHeight + 1) {
    const top = el.getBoundingClientRect().top - doc.getBoundingClientRect().top + doc.scrollTop - (block === 'center' ? doc.clientHeight / 2 - el.clientHeight / 2 : 24);
    doc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  } else el.scrollIntoView({ behavior: 'smooth', block });
}
